import { array, boolean, number, object, string } from 'yup';

// Base schema for common fields
const baseSchema = {
  // Core configuration fields
  active: boolean().required('Active status is required'),

  // Token mint address (Solana token address)
  mint: string()
    .required('Token mint address is required')
    .test('is-valid-solana-address', 'Must be a valid Solana address', value => {
      if (!value) return false;
      // Solana addresses are base58 encoded strings, typically 32-44 characters
      // This is a basic check - a more thorough validation would decode and verify
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaAddressRegex.test(value);
    }),

  // Fee configuration
  fee: string()
    .required('Fee percentage is required')
    .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 100;
    }),

  // Minimum amounts
  min_sol_amount_on_wallet: string()
    .required('Minimum SOL amount on wallet is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),
  min_token_amount_on_wallet: string()
    .required('Minimum token amount on wallet is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),

  // Slippage configuration (string input, converted to number)
  slippage: string()
    .required('Slippage percentage is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0%', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0 && num <= 100;
    }),

  // Target volume (string input, converted to number)
  target_volume_in_usd: string()
    .required('Target volume in USD is required')
    .test('is-valid-positive-number', 'Must be a number greater than or equal to 1', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 1;
    }),

  // Trading interval
  trading_interval: number()
    .required('Trading interval is required')
    .min(1, 'Trading interval must be greater than 0')
    .integer('Must be an integer'),
};

// Schema for create mode (wallet management through creating a new config)
export const createModeSchema = object({
  ...baseSchema,

  // Wallet IDs (managed through creating a new config)
  wallet_ids: array()
    .of(number().required('Wallet ID is required'))
    .required('At least one wallet must be selected')
    .min(1, 'At least one wallet must be selected'),
});

// Schema for edit mode (wallet management through editing a config)
export const editModeSchema = object({
  ...baseSchema,

  // Wallet IDs (wallets already linked to the system)
  wallet_ids: array().of(number().required('Wallet ID is required')),
});

// Main validation schema (for backward compatibility)
export const SolanaSmartLiquidityValidationSchema = createModeSchema;
